import React, { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { bytesToString, formatNamedNumber, formatNumber } from 'js/lib/FormatUtils';
import { MetricRow } from '../metricRows';
import { getAdaptiveTicks } from '../chartTicks';
import { RECORDS_COLOR, ERRORS_COLOR } from './chartColors';
import styles from './MetricsCombinedChart.module.scss';

export { RECORDS_COLOR, ERRORS_COLOR };

export type RechartsTooltipPayload = { value?: number; payload: MetricRow }[];

export type MetricsTooltipRow = {
  key: 'records' | 'errors' | 'size';
  label: string;
  value: string;
};

export type MetricsTooltipModel = {
  date: string;
  rows: MetricsTooltipRow[];
};

export function formatTooltip(row: MetricRow, hideRecords = false): MetricsTooltipModel {
  const rows: MetricsTooltipRow[] = [];

  if (!hideRecords && !row.recordsHidden) {
    rows.push({ key: 'records', label: 'Records', value: formatNumber(row.records) });
  }
  rows.push({ key: 'errors', label: 'Errors', value: formatNumber(row.errors) });
  rows.push({ key: 'size', label: 'Data Volume', value: bytesToString(row.size) || '0 B' });

  return { date: row.date, rows };
}

export function extractTooltipRow(
  active?: boolean,
  payload?: RechartsTooltipPayload,
): MetricRow | null {
  if (!active || !payload?.length) return null;
  return payload[0]?.payload ?? null;
}

const MetricsTooltip = ({
  active,
  payload,
  hideRecords,
}: {
  active?: boolean;
  payload?: RechartsTooltipPayload;
  hideRecords: boolean;
}) => {
  const row = extractTooltipRow(active, payload);
  if (!row) return null;

  const model = formatTooltip(row, hideRecords);

  return (
    <div className={styles.tooltipContainer}>
      <span className={styles.tooltipDate}>{model.date}</span>
      {model.rows.map((item) => (
        <span key={item.key} className={styles.tooltipRow}>
          <span className={styles.tooltipLabel}>{item.label}</span>
          <span className={styles.tooltipValue}>{item.value}</span>
        </span>
      ))}
    </div>
  );
};

type LegendItem = { color: string; label: string; axis: string };

const MetricsLegend = ({ items }: { items: LegendItem[] }) => (
  <ul className={styles.legend} aria-label="Chart legend">
    {items.map((it) => (
      <li key={it.label} className={styles.legendItem}>
        <span className={styles.swatch} style={{ backgroundColor: it.color }} />
        <span>
          {it.label} <span className={styles.axisHint}>({it.axis})</span>
        </span>
      </li>
    ))}
  </ul>
);

export const countTickFormatter = (value: number) => formatNamedNumber(value, -1) || '0';

export type MetricsCombinedChartProps = {
  rows: MetricRow[];
  hideRecords?: boolean;
  height?: number;
};

export const MetricsCombinedChart = (props: MetricsCombinedChartProps) => {
  const { rows, hideRecords = false, height = 300 } = props;

  const ticks = useMemo(() => getAdaptiveTicks(rows), [rows]);

  const legendItems: LegendItem[] = useMemo(
    () => [
      ...(hideRecords ? [] : [{ color: RECORDS_COLOR, label: 'Records', axis: 'left' }]),
      { color: ERRORS_COLOR, label: 'Errors', axis: 'right' },
    ],
    [hideRecords],
  );

  const tooltipContent = useMemo(() => <MetricsTooltip hideRecords={hideRecords} />, [hideRecords]);

  const legendContent = useMemo(() => <MetricsLegend items={legendItems} />, [legendItems]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="label" ticks={ticks} interval="preserveStartEnd" />

        {!hideRecords && (
          <YAxis yAxisId="records" orientation="left" tickFormatter={countTickFormatter} />
        )}
        <YAxis yAxisId="errors" orientation="right" tickFormatter={countTickFormatter} />

        <Tooltip
          cursor={{ fill: 'var(--color-greyscale-100)' }}
          isAnimationActive={false}
          content={tooltipContent}
        />

        <Legend verticalAlign="top" content={legendContent} />

        {!hideRecords && (
          <>
            <Area
              yAxisId="records"
              type="monotone"
              dataKey="records"
              fill={RECORDS_COLOR}
              fillOpacity={0.15}
              stroke="none"
            />
            <Line
              yAxisId="records"
              type="monotone"
              dataKey="records"
              stroke={RECORDS_COLOR}
              dot={false}
            />
          </>
        )}

        <Area
          yAxisId="errors"
          type="monotone"
          dataKey="errors"
          fill={ERRORS_COLOR}
          fillOpacity={0.15}
          stroke="none"
        />
        <Line yAxisId="errors" type="monotone" dataKey="errors" stroke={ERRORS_COLOR} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

MetricsCombinedChart.displayName = 'MetricsCombinedChart';
